// frontend/src/styles/theme.ts
import { alpha, createTheme, PaletteColorOptions } from '@mui/material/styles';
import { esES } from '@mui/material/locale'; // Importar localización española

// Para extender la paleta con nuestro color de acento personalizado
declare module '@mui/material/styles' {
  interface Palette {
    accent: PaletteColorOptions;
  }
  interface PaletteOptions {
    accent?: PaletteColorOptions;
  }
}

// Definición de colores base
const primaryBlueDark = '#0A192F'; // Fondo principal de la página (azul marino muy oscuro)
const paperBlueDark = '#1C293A';  // Fondo para elementos "elevados" como tarjetas, menús (azul marino más claro)
const accentCyan = '#4DD0E1';     // Color de acento principal (cian/turquesa)
const textPrimaryLight = '#F0F8FF'; // Texto principal (blanco fantasma, casi blanco)
const textSecondaryLight = '#B0C4DE'; // Texto secundario (gris azulado claro)

const theme = createTheme({
  palette: {
    mode: 'dark', // Importante: Habilita el modo oscuro de MUI, ajusta colores de texto y fondos por defecto
    primary: {
      main: accentCyan, // Usaremos el acento como primario para botones, links, etc.
      // contrastText: primaryBlueDark, // MUI lo calculará, pero podemos ajustarlo si es necesario
    },
    secondary: { // Podemos redefinir 'secondary' si tenemos otro color de acento, o dejarlo por si se necesita
      main: '#FF9800', // Ejemplo: un naranja suave como secundario, si fuera necesario
      // contrastText: primaryBlueDark,
    },
    accent: { // Nuestro color de acento personalizado
      main: accentCyan,
      light: '#88FFFF', // Una versión más clara del acento
      dark: '#00A8B0',  // Una versión más oscura del acento
      contrastText: primaryBlueDark,
    },
    background: {
      default: primaryBlueDark, // Fondo por defecto de la aplicación
      paper: paperBlueDark,     // Fondo para componentes como Card, Paper, Menu
    },
    text: {
      primary: textPrimaryLight,
      secondary: textSecondaryLight,
      disabled: '#778899', // Gris pizarra claro para texto deshabilitado
    },
    // error: { main: '#FF5252' },
    // warning: { main: '#FFC107' },
    // info: { main: '#2196F3' }, // Podríamos usar accentCyan aquí también
    // success: { main: '#4CAF50' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700, // Bold
    },
    h2: {
      fontWeight: 600, // Semibold
    },
    h3: {
      fontWeight: 600, // Semibold
    },
    h4: { // Usado en "Bienvenido..."
      fontWeight: 600, // Semibold
      fontSize: '2rem', // Ejemplo de ajuste de tamaño
    },
    h5: { // Usado en títulos de tarjeta
      fontWeight: 500, // Medium
      fontSize: '1.5rem',
    },
    h6: { // Usado en "Hola, usuario..."
      fontWeight: 400, // Regular
      fontSize: '1.25rem',
    },
    body1: { // Texto principal, descripciones
      fontWeight: 400, // Regular
    },
    body2: { // Texto secundario, descripciones más pequeñas
      fontWeight: 400, // Regular
      color: textSecondaryLight, // Asegurar que use el color secundario
    },
    button: {
      textTransform: 'none', // Para que los botones no estén en mayúsculas por defecto
      fontWeight: 500,       // Medium
    },
    // Puedes personalizar más variantes si es necesario
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, *': {
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(56,189,248,0.55) #0f172a',
        },
        '*::-webkit-scrollbar': {
          width: 10,
          height: 10,
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: '#0f172a',
          borderRadius: 999,
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(56,189,248,0.55)',
          borderRadius: 999,
          border: '2px solid #0f172a',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(56,189,248,0.85)',
        },
        '*::-webkit-scrollbar-corner': {
          backgroundColor: '#0f172a',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:has(.MuiSelect-select)': {
            borderRadius: 10,
            backgroundColor: '#111e30',
            transition: 'background-color 0.18s ease, box-shadow 0.18s ease',

            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(148,163,184,0.28)',
            },

            '&:hover': {
              backgroundColor: '#17263b',
            },

            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(56,189,248,0.55)',
            },

            '&.Mui-focused:not(.Mui-error)': {
              boxShadow: '0 0 0 3px rgba(56,189,248,0.10)',
            },

            '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
              borderColor: '#38bdf8',
              borderWidth: 1,
            },
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          color: '#f8fafc',
          fontWeight: 700,
        },
        icon: {
          color: '#7dd3fc',
          right: 12,
        },
      },

      defaultProps: {
        MenuProps: {
          PaperProps: {
            sx: {
              mt: 0.75,
              maxHeight: 360,
              borderRadius: 2,
              backgroundColor: '#152238',
              border: '1px solid rgba(56,189,248,0.22)',
              boxShadow: '0 16px 36px rgba(2,6,23,0.55)',

              '& .MuiList-root': {
                p: 0.75,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.35,
              },

              '& .MuiMenuItem-root': {
                minHeight: 40,
                px: 1.5,
                borderRadius: 1.25,
                borderLeft: '3px solid transparent',
                color: '#cbd5e1',
                fontSize: '0.875rem',
                fontWeight: 600,
                transition: 'background-color 0.15s ease',

                '&:hover': {
                  backgroundColor: 'rgba(56,189,248,0.12)',
                  color: '#f8fafc',
                },

                '&.Mui-selected': {
                  backgroundColor: 'rgba(56,189,248,0.17)',
                  borderLeftColor: '#38bdf8',
                  color: '#7dd3fc',
                  fontWeight: 800,
                },

                '&.Mui-selected:hover': {
                  backgroundColor: 'rgba(56,189,248,0.24)',
                },

                '&.Mui-focusVisible': {
                  backgroundColor: 'rgba(56,189,248,0.14)',
                },
              },
            },
          },
        },
      },
    },

    MuiAppBar: {

      defaultProps: {
        elevation: 0, // Mantenemos la eliminación de la sombra por defecto.
      },
      styleOverrides: {
        root: ({ theme }) => ({
          // Las propiedades 'backgroundColor' y 'borderBottom' se eliminan de este override global.
          // Esto nos permite controlar estos estilos dinámicamente dentro del componente MainLayout.tsx,
          // que es un requisito para lograr el efecto de transparencia inicial.
          // La transición entre estados (transparente a sólido) se gestionará a nivel de componente.
          transition: 'background-color 0.3s ease-in-out', // Añadimos una transición suave por defecto.
          fontFamily: theme.typography.fontFamily,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px', // Bordes más redondeados
          // En modo oscuro, la elevación es menos notoria.
          // Podríamos usar un borde sutil si background.paper es muy similar a background.default
          // border: `1px solid ${alpha(textPrimaryLight, 0.12)}`, // Ejemplo de borde sutil
          // O confiar en la diferencia de color entre background.default y background.paper
        },
      },
    },
    MuiCardActionArea: {
        styleOverrides: {
          
           root: {
              '&:hover': {
                backgroundColor: alpha(accentCyan, 0.08), // Un tinte muy sutil del color de acento al pasar el ratón
                // O si prefieres que el texto/icono cambie de color:
                // '& .MuiCardHeader-title, & .MuiSvgIcon-root': { // Asumiendo que usas CardHeader o un SvgIcon
                //   color: theme.palette.accent.main,
                // }
              },
              // Asegurar que la tarjeta tenga altura completa si es necesario para el efecto
              height: '100%', 
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start', // Para alinear el contenido si la tarjeta tiene altura fija
            }
        }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px', // Botones también más redondeados
        },
        // containedPrimary: { // Estilos específicos para el botón primario
        //   color: primaryBlueDark, // Para asegurar buen contraste con el fondo accentCyan del botón
        // },
      },
    },
    // Puedes añadir overrides para MuiMenu, MuiMenuItem, etc., según sea necesario
  },
  spacing: 8, // Define la unidad de espaciado base (8px es común)
},
esES // Aplicar la localización española
);

export default theme;